import { datadogRum } from '@datadog/browser-rum';

/**
 * Check if RUM should be enabled based on environment
 * @param {import('../config/config').HeliosEnvVars} config - Application configuration object
 * @returns {boolean} - True if RUM should be enabled
 */
function shouldEnableRum(config) {
  const clientToken = config.datadog_rum_client_token?.trim();
  const applicationId = config.datadog_rum_application_id?.trim();

  // Must have credentials configured
  if (!clientToken || !applicationId) {
    return false;
  }

  // Only enable on production environment
  const env = config.server_env?.toLowerCase();
  return env === 'production' || env === 'prod';
}

/**
 * Initialize Datadog RUM (Real User Monitoring)
 * @param {import('../config/config').HeliosEnvVars} config - Application configuration object
 */
export default function initDatadogRum(config) {
  if (!shouldEnableRum(config)) {
    console.warn('Datadog RUM is not enabled on this environment');
    return;
  }

  // Helper function to redact sensitive data (JWT tokens)
  function redactSensitiveData(str) {
    if (!str) {
      return str;
    }
    return str.replace(
      /[a-zA-Z0-9_=]{36}\.[a-zA-Z0-9_=]{51}\.[a-zA-Z0-9_\-+/=]{43}/u,
      'JWT_REDACTED'
    );
  }

  try {
    datadogRum.init({
      applicationId: config.datadog_rum_application_id,
      clientToken: config.datadog_rum_client_token,
      site: config.datadog_rum_site,
      service: config.datadog_rum_service,
      env: config.server_env || 'development',
      version: config.nova_version,
      // Session sample rate (0 to 100)
      // 100% in development, configurable in production
      sessionSampleRate: config.datadog_rum_session_sample_rate ?? 10,
      // Session replay sample rate (0 to 100); the represents the % on top of the sessionSampleRate
      // Set to 0 if you don't want session replay
      sessionReplaySampleRate: 0,
      // Track user interactions
      trackUserInteractions: config.datadog_rum_track_user_interactions ?? true,
      // Track resources (images, scripts, etc.)
      trackResources: config.datadog_rum_track_resources ?? false,
      // Track long tasks
      trackLongTasks: config.datadog_rum_track_long_tasks ?? false,
      // Default privacy level
      defaultPrivacyLevel: config.datadog_rum_default_privacy_level ?? 'mask-user-input',
      // Before send callback to redact sensitive information
      beforeSend(event) {
        // Redact sensitive data from URLs
        if (event.view?.url) {
          event.view.url = redactSensitiveData(event.view.url);
        }

        // Redact sensitive data from resource URLs
        if (event.resource?.url) {
          event.resource.url = redactSensitiveData(event.resource.url);
        }

        // Redact sensitive data from action target URLs
        if (event.action?.target?.name) {
          event.action.target.name = redactSensitiveData(event.action.target.name);
        }

        // Redact sensitive data from error messages
        if (event.error?.message) {
          event.error.message = redactSensitiveData(event.error.message);
        }

        // Redact sensitive data from error stack
        if (event.error?.stack) {
          event.error.stack = redactSensitiveData(event.error.stack);
        }

        return true;
      }
    });
  } catch (error) {
    console.error('Error initializing Datadog RUM:', error);
  }
}

/**
 * Check if Datadog RUM is initialized
 * @returns {boolean} - True if RUM is initialized
 */
function isRumInitialized() {
  try {
    // getInternalContext returns undefined if RUM is not initialized
    return datadogRum.getInternalContext() !== undefined;
  } catch {
    return false;
  }
}

/**
 * Set user context for Datadog RUM
 * @param {object|null} user - User object with id, email, etc.
 */
export function setDatadogRumUser(user) {
  // Skip if RUM is not initialized
  if (!isRumInitialized()) {
    return;
  }

  if (!user?.id) {
    datadogRum.clearUser();
    return;
  }

  datadogRum.setUser({
    id: user.id
  });
}
